import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const CACHE_DURATION = 3 * 60 * 1000; // 3 minutos en milisegundos
const RETRY_DELAY = 2000; // 2 segundos de espera entre reintentos
const MAX_RETRIES = 3; // Máximo número de reintentos

// Monedas que CoinGecko soporta como vs_currency directamente.
const COINGECKO_NATIVE = ['USD', 'EUR', 'GBP', 'BRL', 'CAD', 'MXN'];
// Monedas que obtenemos vía Yadio (USD -> fiat de mercado).
const YADIO_CURRENCIES = ['COP', 'UYU', 'PEN'];
// ARS se maneja aparte con el dólar "blue" de Bluelytics.

const isFresh = (entry) =>
  entry && typeof entry.rate === 'number' && !Number.isNaN(entry.rate) &&
  Date.now() - entry.timestamp < CACHE_DURATION;

const useExchangeRates = (currencies) => {
  // Normalizamos la entrada a un array de códigos únicos en mayúsculas.
  const currencyList = useMemo(() => {
    const list = Array.isArray(currencies) ? currencies : [currencies];
    return Array.from(
      new Set(list.filter(Boolean).map((c) => String(c).toUpperCase()))
    );
  }, [currencies]);

  const currencyKey = currencyList.slice().sort().join(',');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratesCache, setRatesCache] = useState(() => {
    try {
      const savedCache = localStorage.getItem('ratesCache');
      return savedCache ? JSON.parse(savedCache) : {};
    } catch {
      return {};
    }
  });

  // Guardamos la caché en un ref para leer siempre el valor más reciente
  // dentro de fetchRates sin recrear el callback en cada actualización.
  const cacheRef = useRef(ratesCache);
  cacheRef.current = ratesCache;

  const fetchWithRetry = useCallback(async (url, options = {}, retries = 0) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 segundos timeout

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        mode: 'cors',
        headers: {
          Accept: 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (response.status === 429) {
        throw new Error('RATE_LIMIT');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      clearTimeout(timeoutId);

      if (err.message === 'RATE_LIMIT' && retries < MAX_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * Math.pow(2, retries))
        );
        return fetchWithRetry(url, options, retries + 1);
      }

      if (retries < MAX_RETRIES && err.name !== 'AbortError') {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return fetchWithRetry(url, options, retries + 1);
      }
      throw err;
    }
  }, []);

  const updateCache = useCallback((newEntries) => {
    try {
      setRatesCache((prev) => {
        const newCache = { ...prev, ...newEntries };
        localStorage.setItem('ratesCache', JSON.stringify(newCache));
        return newCache;
      });
    } catch (err) {
      console.error('Error updating cache:', err);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const cache = cacheRef.current;
    const needed = currencyList.filter((c) => !isFresh(cache[c]));

    if (needed.length === 0) {
      setIsLoading(false);
      return;
    }

    const cgNeeded = needed.filter((c) => COINGECKO_NATIVE.includes(c));
    const yadioNeeded = needed.filter((c) => YADIO_CURRENCIES.includes(c));
    const arsNeeded = needed.includes('ARS');

    // CoinGecko necesita 'usd' para poder derivar ARS (blue).
    const vsSet = new Set(cgNeeded.map((c) => c.toLowerCase()));
    if (arsNeeded) vsSet.add('usd');

    const requests = [];

    if (vsSet.size > 0) {
      const vs = Array.from(vsSet).join(',');
      requests.push(
        fetchWithRetry(
          `https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=${vs}`
        ).then((data) => ({ source: 'coingecko', data }))
      );
    }

    if (arsNeeded) {
      requests.push(
        fetchWithRetry('https://api.bluelytics.com.ar/v2/latest').then((data) => ({
          source: 'bluelytics',
          data,
        }))
      );
    }

    if (yadioNeeded.length > 0) {
      requests.push(
        fetchWithRetry('https://api.yadio.io/exrates/USD').then((data) => ({
          source: 'yadio',
          data,
        }))
      );
    }

    try {
      const results = await Promise.all(requests);
      const bySource = {};
      results.forEach((r) => {
        bySource[r.source] = r.data;
      });

      const timestamp = Date.now();
      const newEntries = {};

      const cg = bySource.coingecko;
      if (cg && cg.bitcoin) {
        cgNeeded.forEach((c) => {
          const value = cg.bitcoin[c.toLowerCase()];
          if (typeof value === 'number' && !Number.isNaN(value)) {
            newEntries[c] = { rate: value, timestamp };
          }
        });
      }

      if (arsNeeded) {
        const btcUsd = cg && cg.bitcoin ? cg.bitcoin.usd : undefined;
        const usdToArs =
          bySource.bluelytics && bySource.bluelytics.blue
            ? bySource.bluelytics.blue.value_avg
            : undefined;
        if (
          typeof btcUsd === 'number' &&
          typeof usdToArs === 'number' &&
          !Number.isNaN(btcUsd) &&
          !Number.isNaN(usdToArs)
        ) {
          newEntries.ARS = { rate: btcUsd * usdToArs, timestamp };
        }
      }

      if (yadioNeeded.length > 0 && bySource.yadio) {
        const yadio = bySource.yadio;
        const btcUsd = typeof yadio.BTC === 'number' ? yadio.BTC : undefined;
        const usdRates = yadio.USD || {};
        if (typeof btcUsd === 'number' && !Number.isNaN(btcUsd)) {
          yadioNeeded.forEach((c) => {
            const usdToFiat = usdRates[c];
            if (typeof usdToFiat === 'number' && !Number.isNaN(usdToFiat)) {
              newEntries[c] = { rate: btcUsd * usdToFiat, timestamp };
            }
          });
        }
      }

      if (Object.keys(newEntries).length > 0) {
        updateCache(newEntries);
      }

      // Si alguna moneda pedida no se pudo resolver, lo marcamos como error
      // (pero conservamos las que sí funcionaron).
      const stillMissing = needed.filter(
        (c) => !newEntries[c] && !isFresh(cache[c])
      );
      if (stillMissing.length > 0) {
        setError(`No se pudo obtener la tasa de: ${stillMissing.join(', ')}`);
      }
    } catch (err) {
      console.error('Error fetching exchange rates:', err);
      setError(err.message);
      // La caché (aunque esté expirada) se usa como fallback al construir `rates`.
    } finally {
      setIsLoading(false);
    }
  }, [currencyKey, fetchWithRetry, updateCache]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Construimos el mapa de tasas devuelto a partir de la caché (fresca o no,
  // para que exista fallback offline).
  const rates = useMemo(() => {
    const map = {};
    currencyList.forEach((c) => {
      const entry = ratesCache[c];
      if (entry && typeof entry.rate === 'number' && !Number.isNaN(entry.rate)) {
        map[c] = entry.rate;
      }
    });
    return map;
  }, [ratesCache, currencyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearCache = useCallback(() => {
    setRatesCache({});
    localStorage.removeItem('ratesCache');
  }, []);

  return {
    rates,
    isLoading,
    error,
    clearCache,
    refresh: fetchRates,
  };
};

export default useExchangeRates;
